import {
    isExperienceProduct,
    isGiftOfferProduct,
    isMembershipProduct,
    isMerchandiseProduct,
    isPackageProduct,
    isPartnerPackageProduct,
} from "common";
import { isEmpty, maxBy, toPairs, uniqBy } from "lodash";
import {
    Button,
    ButtonItem,
    ButtonObjectType,
    ButtonType,
    CheckoutPreference,
    CheckoutType,
    FeatureRole,
    FLOW,
    PopupCheckoutType,
    Product,
    ProductObject,
    Seller,
    UserRole,
} from "types";
import { checkoutUri, x2CheckoutUri } from "../api/checkoutUri";
import { giftUri } from "../api/giftUri";
import { hasFeature, hasX2CheckoutWithFullProbability } from "./Seller";
import { queryClient } from "@/api/queryClient";
import { AccountState } from "@/hooks/useAccount";
import { getProductFromQueryCache } from "@/models/Product";
import { getSeller } from "@/utils/getSeller";

const DEFAULT_FLOW: FLOW = "mvp";

export const hasPartnerProducts = (displayedProducts: readonly Product[], sellerId: string) => {
    return displayedProducts.some((product) => {
        return isPartnerPackageProduct(product) || (isExperienceProduct(product) && product.seller.id !== sellerId);
    });
};

export const getActiveButtonsByType = (buttons: Button[], buttonType: ButtonType) => {
    return buttons.filter((button) => button.type === buttonType && !button.archivedAt);
};

export const getButtonUrl = (
    seller: Seller,
    checkoutType: string,
    buttonType: string = ButtonType.SINGLE,
    product?: Product,
    button?: Button,
) => {
    const isPackage = isPackageProduct(product);
    const isPartnerPackage = isPartnerPackageProduct(product);
    const isMembership = isMembershipProduct(product);
    let url = "";

    if (buttonType.endsWith("_x2")) {
        const isEmbedded = checkoutType === CheckoutType.EMBEDDED;
        const flow = getFlowFromWeights(seller.preferences?.checkout);
        url = `${x2CheckoutUri}/flows/${flow}?button=${button?.id ?? product?.id ?? seller.id}`;
        if (button && button.type === ButtonType.TIMELINE) {
            url += "&view=timeline";
        } else if (button && !isEmpty(button?.items)) {
            const hasAllGifts = button.items.every((item) => item.object === ButtonObjectType.GIFT_OFFER);
            url += isEmbedded && !hasAllGifts ? "&view=grid&type=day" : "&view=grid";
        }

        const isPartnerExperience = isExperienceProduct(product) && product.seller.id !== seller.id;
        if (isPartnerExperience) {
            url += `&seller=${seller.id}`;
        }

        return url;
    }

    switch (buttonType) {
        case ButtonType.GIFT:
            url = `${giftUri}?button=${button?.id}&_=${Date.now()}`;
            break;
        case ButtonType.SINGLE:
            if (isPackage) {
                url = `${checkoutUri}#seller/${seller.id}/packages/${product?.id}`;
            } else if (isPartnerPackage) {
                url = `${checkoutUri}#seller/${seller.id}/partnerPackages/${product?.id}`;
            } else if (isMembership) {
                url = `${checkoutUri}#seller/${seller.id}/memberships/${product?.id}`;
            } else {
                url = `${checkoutUri}#seller/${seller.id}/experiences/${product?.id}`;
            }

            break;
        case ButtonType.ALL_PRODUCTS:
            url = `${checkoutUri}#seller/${seller.id}`;
            break;
        default: {
            url = `${checkoutUri}#buttons/${button?.id}?cache=${Date.now()}`;
        }
    }

    if (url) {
        url += url.includes("?") ? "&openExternal=true" : "?openExternal=true";
    }

    if (checkoutType === CheckoutType.EMBEDDED) {
        url += url.includes("?") ? "&embeddedCheckout=true" : "?embeddedCheckout=true";
    }

    if (hasFeature(seller, FeatureRole.MULTI_LANGUAGE_SUPPORT)) {
        url += url.includes("?") ? "&locale=auto" : "?locale=auto";
    }

    return url;
};

export const getFlowFromWeights = (preferences?: CheckoutPreference): FLOW => {
    const flowWeights = preferences?.flowWeights;
    if (!flowWeights) {
        return DEFAULT_FLOW;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const flowWithHighestWeight = maxBy(toPairs(flowWeights), ([_, weight]) => weight)?.[0]; // Gets the key which has the highest value
    return (flowWithHighestWeight as FLOW) ?? DEFAULT_FLOW;
};

export const getButtonCode = (
    checkoutType: string,
    popupType: string,
    buttonType: string = ButtonType.CHECKOUT,
    product?: Product,
    button?: Button,
) => {
    const seller = getSeller();
    if (checkoutType === CheckoutType.URL) {
        return getButtonUrl(seller, checkoutType, buttonType, product, button);
    }

    const isPackage = isPackageProduct(product);
    const isMembership = isMembershipProduct(product);
    const isPartnerPackage = isPartnerPackageProduct(product);
    let buttonClass = buttonType.includes(ButtonType.GIFT) ? "xola-gift" : "xola-checkout";

    if (checkoutType === CheckoutType.EMBEDDED) {
        buttonClass = "xola-embedded-checkout";
    }

    if (popupType === PopupCheckoutType.CUSTOM) {
        buttonClass += " xola-custom";
    }

    let buttonMarkupContent = `<div class="${buttonClass}"`;

    if (buttonType.includes(ButtonType.SINGLE)) {
        buttonMarkupContent += ` data-seller="${seller.id}" data-version="2"`;
        if (isPackage) {
            buttonMarkupContent += ` data-package="${product?.id}"`;
        } else if (isPartnerPackage) {
            buttonMarkupContent += ` data-partner-package="${product?.id}"`;
        } else if (isMembership) {
            buttonMarkupContent += ` data-membership="${product?.id}"`;
        } else {
            buttonMarkupContent += ` data-experience="${product?.id}"`;
        }
    } else if (buttonType.includes(ButtonType.ALL_PRODUCTS)) {
        buttonMarkupContent += ` data-seller="${seller.id}" data-version="2"`;
    } else {
        buttonMarkupContent = `<div class="${buttonClass}" data-button-id="${button?.id ?? "N/A"}"`;
    }

    if (hasFeature(seller, FeatureRole.MULTI_LANGUAGE_SUPPORT)) {
        buttonMarkupContent += ` data-locale="auto"`;
    }

    buttonMarkupContent += "></div>";
    return buttonMarkupContent;
};

const isButtonItemSupportedForX2Checkout = (item: ButtonItem) => {
    const supported = [
        ButtonObjectType.EXPERIENCE,
        ButtonObjectType.GIFT_OFFER,
        ButtonObjectType.PACKAGE,
        ButtonObjectType.MEMBERSHIP,
        ButtonObjectType.PARTNER_PACKAGE,
        ButtonObjectType.WARE,
    ];
    return supported.includes(item.object as ButtonObjectType);
};

const isProductSupportedForX2Checkout = (product: Product) => {
    return (
        isExperienceProduct(product) ||
        isGiftOfferProduct(product) ||
        isPackageProduct(product) ||
        isMembershipProduct(product) ||
        isPartnerPackageProduct(product) ||
        isMerchandiseProduct(product)
    );
};

// Show the X2 Checkout button for Xola admins or if the seller has 100% probability for checkout
// Ensure it's not shown for Packages
export const isX2CheckoutSupported = (
    button?: { type: ButtonType; items?: ButtonItem[] },
    product?: Product | undefined,
) => {
    if (product && !isProductSupportedForX2Checkout(product)) {
        return false;
    }

    const type = button?.type;

    const { user: me } = queryClient.getQueryData<AccountState>(["account"]) ?? { user: null };
    const isXolaAdmin = me?.roles?.includes(UserRole.ADMIN);

    if (!hasX2CheckoutWithFullProbability() && !isXolaAdmin) {
        // Seller does not have X2 Checkout with full probability and user is not an admin
        // no need todo further checks for button type, just return early
        return false;
    }
    if (type === ButtonType.ALL_PRODUCTS && hasX2CheckoutWithFullProbability()) {
        // All Products button is supported only when seller has X2 Checkout with full probability
        return true;
    }

    if (button?.items && button.items.length > 0) {
        // Check if all products in the button are supported
        for (const item of button.items) {
            if (!isButtonItemSupportedForX2Checkout(item)) {
                return false;
            }

            const product = getProductFromQueryCache(item.product?.id);
            if (!product || !isProductSupportedForX2Checkout(product)) {
                return false;
            }
        }
    }

    // Product checks have passed (or no items to check), we return true based on probability or admin role
    const canShowForXolaAdmin = isXolaAdmin && hasFeature(getSeller(), FeatureRole.X2_CHECKOUT);
    return hasX2CheckoutWithFullProbability() || canShowForXolaAdmin;
};

const checkoutIframeId = `#xola-checkout-preview-iframe`;
export const openCheckoutIframe = (url: string) => {
    if (document.querySelector(checkoutIframeId)) {
        document.querySelector(checkoutIframeId)?.remove();
    }

    const iframe = document.createElement("iframe");
    iframe.id = checkoutIframeId.slice(1);
    iframe.src = url + "&origin=" + window.location.origin; // origin attribute so we can receive the 'close' event
    iframe.style.cssText = `position: fixed; top: 0; left: 0; width: 100%; height: 100%; border: none; z-index: 9999;`;
    iframe.setAttribute("frameBorder", "0");
    iframe.setAttribute("allow", "payment");
    iframe.setAttribute("allowPaymentRequest", "true");
    document.body.append(iframe);

    const removeIframe = () => {
        const iframeElement = document.querySelector(checkoutIframeId);
        if (iframeElement) {
            iframeElement.remove();
            window.removeEventListener("message", handleMessage);
            iframe.removeEventListener("error", handleError);
            console.debug(`${checkoutIframeId} removed from DOM & listener cleaned up`);
        }
    };

    const handleMessage = (event: MessageEvent) => {
        if (!event.origin || !url.startsWith(event.origin)) {
            // Message did not come from the checkout app
            return;
        }

        try {
            const message = JSON.parse(event.data);
            if (message.event === "close") {
                removeIframe();
            }
        } catch (error) {
            console.error("Error parsing message data", error);
        }
    };

    const handleError = (event: Event) => {
        console.log("Error loading iframe", event);
        removeIframe();
    };

    window.addEventListener("message", handleMessage);
    iframe.addEventListener("error", handleError);
};

export const getButtonObjectType = (product: Product) => {
    switch (product.object) {
        case ProductObject.GIFT_OFFER:
            return ButtonObjectType.GIFT_OFFER;
        case ProductObject.PACKAGE:
            return ButtonObjectType.PACKAGE;
        case ProductObject.MEMBERSHIP:
            return ButtonObjectType.MEMBERSHIP;
        case ProductObject.PARTNER_PACKAGE:
            return ButtonObjectType.PARTNER_PACKAGE;
        case ProductObject.WARE:
            return ButtonObjectType.WARE;
        case ProductObject.COLLECTION:
            return ButtonObjectType.COLLECTION;
        default:
            return ButtonObjectType.EXPERIENCE;
    }
};

export const getInitialValue = (
    selectedButtons: string[] | undefined,
    products: Product[],
    selectedEditButton: Button | undefined,
    type: "notDisplayed" | "displayed",
) => {
    const selectedProductIds = selectedEditButton?.items.map((item: ButtonItem) => item.product.id) ?? [];

    if (!selectedButtons && !selectedEditButton) {
        return type === "displayed" ? [] : products;
    }

    if (type === "displayed") {
        if (selectedEditButton) {
            const sortedItems = [...selectedEditButton.items].sort((a, b) => a.sequence - b.sequence);
            return sortedItems
                .map((item) => products.find((product) => product.id === item.product.id))
                .filter((product): product is Product => product !== undefined);
        }

        return products.filter((product) => selectedProductIds.includes(product.id));
    }

    return products.filter((product) => !selectedProductIds.includes(product.id));
};

export const filterInsertListingFromButton = (
    buttons: Button[],
    selectedButtons: string[],
    { products, displayedProducts }: { products: Product[]; displayedProducts: Product[] },
) => {
    const selectedButtonObjects = buttons.filter((button) => selectedButtons.includes(button.id));
    const newDisplayedProducts: Product[] = [];

    for (const button of selectedButtonObjects) {
        for (const item of button.items) {
            const product = [...products, ...displayedProducts].find((p) => p.id === item.product.id);
            if (product) {
                newDisplayedProducts.push(product);
            }
        }
    }

    if (newDisplayedProducts.length === 0) {
        return [products, displayedProducts];
    }

    const uniqNewProducts = uniqBy(newDisplayedProducts, (p) => p.id);
    const ids = new Set(uniqNewProducts.map((p) => p.id));
    const uniqDisplayedProducts = uniqBy([...displayedProducts, ...newDisplayedProducts], "id");
    const remainingProducts = products.filter((p) => !ids.has(p.id));
    return [remainingProducts, uniqDisplayedProducts];
};
